import { oc } from "@orpc/contract";
import { z } from "zod";

// ============================================================================
// Shared Schemas
// ============================================================================

const InvoiceFileSchema = z.object({
  id: z.string(),
  name: z.string(),
  mimeType: z.string().nullable().optional(),
  fileSizeBytes: z.number().nullable().optional(),
});

const SupplierRefSchema = z.object({
  id: z.string(),
  name: z.string(),
});

const LineItemDateRangeSchema = z
  .object({
    earliest: z.string(),
    latest: z.string(),
  })
  .nullable()
  .optional();

// ============================================================================
// Update Rebate Invoice - Update invoice number for a rebate invoice
// ============================================================================

export const UpdateRebateInvoiceRequestSchema = z.object({
  invoiceId: z.string().uuid("Invalid invoice ID format"),
  invoiceNumber: z.string().nullable(),
});

export const UpdateRebateInvoiceResponseSchema = z.object({
  data: z.object({
    id: z.string().uuid(),
    invoiceNumber: z.string().nullable(),
  }),
});

// ============================================================================
// List Invoices by Agreement
// ============================================================================

export const ListInvoicesByAgreementRequestSchema = z.object({
  agreementId: z.string().uuid("Invalid agreement ID format"),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  limit: z.number().int().min(1).max(1000).optional().default(100),
  offset: z.number().int().min(0).optional().default(0),
});

const InvoiceSummarySchema = z.object({
  id: z.string(),
  invoiceNumber: z.string().nullable(),
  invoiceDate: z.string(),
  createdAt: z.string(),
  totalAmount: z.number(),
  totalRebateableAmount: z.number(),
  originalTotalAmount: z.number(),
  currency: z.string(),
  lineItemsCount: z.number(),
  totalLineItemValue: z.number(),
  lineItemDateRange: LineItemDateRangeSchema,
  divisionBreakdown: z.array(
    z.object({
      divisionId: z.string(),
      divisionName: z.string(),
      totalValue: z.number(),
      itemCount: z.number(),
    }),
  ),
  supplier: SupplierRefSchema,
  files: z.object({
    originalFiles: z.array(InvoiceFileSchema),
    transformedFile: InvoiceFileSchema.optional(),
  }),
});

export const ListInvoicesByAgreementResponseSchema = z.object({
  success: z.boolean(),
  data: z.object({
    invoices: z.array(InvoiceSummarySchema),
    agreement: z.object({
      id: z.string(),
      name: z.string(),
      supplier: SupplierRefSchema,
    }),
    pagination: z.object({
      limit: z.number(),
      offset: z.number(),
      total: z.number(),
    }),
  }),
});

// ============================================================================
// Get Invoice Associations (pre-delete check)
// ============================================================================

export const GetInvoiceAssociationsRequestSchema = z.object({
  invoiceId: z.string().uuid("Invalid invoice ID format"),
});

export const GetInvoiceAssociationsResponseSchema = z.object({
  data: z.object({
    invoiceId: z.string(),
    invoiceNumber: z.string().nullable(),
    totalAmount: z.number(),
    currency: z.string(),
    lineItemCount: z.number(),
    documentCount: z.number(),
    associationCount: z.number(),
    associations: z.array(
      z.object({
        agreementId: z.string(),
        agreementName: z.string(),
        agreementStatus: z.enum(["draft", "finalized"]),
        supplier: SupplierRefSchema,
      }),
    ),
    createdAt: z.string(),
    lineItemDateRange: LineItemDateRangeSchema,
  }),
});

// ============================================================================
// Delete Rebate Invoice
// ============================================================================

export const DeleteRebateInvoiceRequestSchema = z.object({
  invoiceId: z.string().uuid("Invalid invoice ID format"),
  agreementId: z.string().uuid("Invalid agreement ID format"),
  force: z.boolean().optional().default(false),
});

export const DeleteRebateInvoiceResponseSchema = z.object({
  data: z.object({
    mode: z.enum(["remove_association", "cascade_delete", "force_delete"]),
    invoiceId: z.string(),
    agreementId: z.string(),
    deleted: z.object({
      associations: z.number(),
      invoice: z.boolean(),
      lineItems: z.number(),
      documents: z.number(),
    }),
    remaining: z
      .object({
        associationCount: z.number(),
        affectedAgreements: z.array(z.string()),
      })
      .optional(),
  }),
});

// ============================================================================
// Contract Definitions
// ============================================================================

export const rebateTransactionRecordsContracts = {
  update: oc
    .input(UpdateRebateInvoiceRequestSchema)
    .output(UpdateRebateInvoiceResponseSchema)
    .route({ method: "POST" }),

  listByAgreement: oc
    .input(ListInvoicesByAgreementRequestSchema)
    .output(ListInvoicesByAgreementResponseSchema)
    .route({ method: "GET" }),

  getAssociations: oc
    .input(GetInvoiceAssociationsRequestSchema)
    .output(GetInvoiceAssociationsResponseSchema)
    .route({ method: "GET" }),

  delete: oc
    .input(DeleteRebateInvoiceRequestSchema)
    .output(DeleteRebateInvoiceResponseSchema)
    .route({ method: "POST" }),
} as const;

// ============================================================================
// Type Exports
// ============================================================================

export type UpdateRebateInvoiceRequest = z.infer<
  typeof UpdateRebateInvoiceRequestSchema
>;
export type UpdateRebateInvoiceResponse = z.infer<
  typeof UpdateRebateInvoiceResponseSchema
>;
export type ListInvoicesByAgreementRequest = z.infer<
  typeof ListInvoicesByAgreementRequestSchema
>;
export type ListInvoicesByAgreementResponse = z.infer<
  typeof ListInvoicesByAgreementResponseSchema
>;
export type GetInvoiceAssociationsRequest = z.infer<
  typeof GetInvoiceAssociationsRequestSchema
>;
export type GetInvoiceAssociationsResponse = z.infer<
  typeof GetInvoiceAssociationsResponseSchema
>;
export type DeleteRebateInvoiceRequest = z.infer<
  typeof DeleteRebateInvoiceRequestSchema
>;
export type DeleteRebateInvoiceResponse = z.infer<
  typeof DeleteRebateInvoiceResponseSchema
>;
